import React, { useState } from 'react'
import { useSpring } from '@react-spring/web'

import { useTranslation } from 'react-i18next'
import Tracking from '../../utils/tracking'
import Cta from '../Cta'
import Button from '../Button'
import { ContentMedium } from '../Content'

import IconRefresh from '../../assets/icons/refresh.svg'
import IconEye from '../../assets/icons/eye.svg'
import IconEyeClosed from '../../assets/icons/eye-close.svg'

import { customCtaCard } from '../../style'
import {
  CardWrapper,
  Card,
  CardImageWrapper,
  CardPerson,
  CardTrait,
  AnimatedCard,
  CardControls,
  CardFooter,
  CardTitleBack,
  CardFlairText,
} from './style'


const GenericCard = ({
  cardContent,
  cardType,
  onRefresh,
  key,
  style,
  children,
  animationDisabled
}) => {
  const { t, i18n } = useTranslation()
  const isGerman = i18n.language === 'de_DE'
  const [flipped, setFlipped] = useState(false)

  const cardConfig = {
      mass: 5,
      tension: 500,
      friction: 80,
      duration: animationDisabled ? 0 : undefined,
    }

  const frontSpring = useSpring({
    opacity: flipped ? 0 : 1,
    transform: `perspective(600px) rotateY(${flipped ? 180 : 0}deg)`,
    config: cardConfig,
  })

  const backSpring = useSpring({
    opacity: flipped ? 1 : 0,
    transform: `perspective(600px) rotateY(${flipped ? 0 : -180}deg)`,
    config: cardConfig,
  })

  function trackViewNeeds(cardType, cardContent, state) {
    // Only run when viewing the needs
    if (state) return false

    // Tracking [#3, #4]
    Tracking.trackEvent({
      event: `game.click.view-${cardType}-needs`,
      [cardType]: Tracking.utils.formatCards(cardType, cardContent)
    })

  }

  return (
    cardContent && (
      <CardWrapper data-testid={`${cardType}-card`}>
        <AnimatedCard
          key={key}
          style={style}
          onClick={() => {
            setFlipped((state) => {
              trackViewNeeds(cardType, cardContent, state)
              return !state
            })
          }}
        >
          <AnimatedCard data-testid={`${cardType}-card-front`} style={{ ...frontSpring, position: 'absolute' }}>
            {children}
          </AnimatedCard>
          <AnimatedCard data-testid={`${cardType}-card-back`} style={{ ...backSpring, position: 'absolute' }}>
            <Card type="consider">
              <CardTitleBack data-testid={`${cardType}-card-title-back`}>{t('content.consider')}</CardTitleBack>
              {cardContent.considerations.map((consideration) => {
                return (
                  <ContentMedium key={consideration} data-testid={`${cardType}-card-consideration`}>
                    {consideration}
                  </ContentMedium>
                )
              })}

              {cardContent.stat && (
                <CardFooter>
                  {cardContent.stat}
                </CardFooter>
              )}
            </Card>
          </AnimatedCard>
        </AnimatedCard>

        <CardControls $isGerman={isGerman}>
          <Button
            onClick={() => {
              trackViewNeeds(cardType, cardContent)
              setFlipped((state) => !state)
            }}
            data-testid={`${cardType}-card-view-needs`}
          >
            <img src={flipped ? IconEyeClosed : IconEye} alt="" />
            <Cta
              text={t('content.viewNeeds')}
              customStyle={customCtaCard}
              isGerman={isGerman}
            />
          </Button>
          <Button
            onClick={() => {
              // Get new card
              var newCard = onRefresh();

              // Tracking [#5, #6]
              Tracking.trackEvent({
                event: `game.click.swap-${cardType}-card`,
                [cardType]: Tracking.utils.formatCards(cardType, newCard)
              })
            }}
            data-testid={`${cardType}-card-swap-card`}
          >
            <img src={IconRefresh} alt="" />
            <Cta text={t('content.swapCard')} customStyle={customCtaCard} isGerman={isGerman} />
          </Button>
        </CardControls>
      </CardWrapper>
    )
  )
}

export const PersonCard = (props) => {
  const { person } = props

  return (
    person && (
      <GenericCard {...props} cardType="person" cardContent={person}>
        <Card type="person">
          <CardPerson data-testid='person-name'>
            {person.name}, {person.age}
            <br />
            <CardFlairText data-testid="person-attribute">{person.personalityTraits}</CardFlairText>
          </CardPerson>
          <CardImageWrapper>
            <img src={person.image} alt="" style={{ width: '100%' }} data-testid="person-card-image" />
          </CardImageWrapper>
        </Card>
      </GenericCard>
    )
  )
}

export const TraitCard = (props) => {
  const { trait } = props

  return (
    trait && (
      <GenericCard {...props} cardType="trait" cardContent={trait}>
        <Card type="trait">
          <CardImageWrapper>
            <img src={trait.image} alt="" style={{ width: '100%' }} data-testid='trait-card-image' />
          </CardImageWrapper>
          <CardTrait>
            <CardFlairText data-testid='trait'>{trait.description}</CardFlairText>
          </CardTrait>
        </Card>
      </GenericCard>
    )
  )
}
